import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { number, object } from "yup";
import classNames from "classnames/bind";

import ProductDetails from "./ProductDetails";

import { CatalogAPI } from "providers/CatalogAPI";
import { OrderStorage } from "storage/OrderStorage";
import Categories from "./Categories";
import { If } from "shared/components/control-statements";
import { Title, Button, Loading, Hairline } from "@components";
import { ArrowLeftIcon } from "shared/components/Icons";
import { Currency } from "shared/utils/Currency";
import { CatalogHelpers } from "shared/tools/CatalogHelpers";
import { buildSteps, prepareProduct, findQuestion } from "./Step";

import styles from "../../../styles/service/service.module.scss";
import Cart from "./Cart";
import { Catalog } from "shared/utils/Catalog";
import dayjs from "dayjs";
import ConfirmOrder from "./ConfirmOrder";
import { InputSearch } from "shared/components/InputSearch";
import { CustomerAPI } from "providers/CustomerAPI";
import debounce from "lodash.debounce";
import { Phone } from "shared/utils/Phone";
import { BucketsAPI } from "providers/BucketAPI";
import { EnvironmentStorage } from "shared/tools/EnvironmentStorage";
import { EnvironmentsAPI } from "providers/EnvironmentsAPI";
import { DeliveryType } from "@olga-food/schemas/lib/classes/schemas/order/delivery/Delivery";
import AddCustomer from "./AddCustomer";
import AddAddress from "./AddAddress";
import { CustomerDrawer } from "../oraculo/customers/CustomerDrawer";
import { useOutsideClick } from "shared/hooks/useOutsideClick";

const executeOnSchema = (item, callback) => {
  for (let field of [
    "items",
    "extra_ingredients",
    "edges",
    "doughs",
    "sizes",
  ]) {
    if (item[field] && item[field].length > 0) {
      for (let children of item[field]) {
        executeOnSchema(children, callback);
        callback(children, item, field);
      }
    }
  }
};

export const formatAddress = (a) => {
  if (!a) return "";

  const { address, number, region, city, state } = a;

  const parts = [];

  if (address) parts.push(address);
  if (number) parts.push(number);
  if (region) parts.push(region);
  if (city) parts.push(city);
  if (state) parts.push(state);

  return parts.join(", ");
};

const css = classNames.bind(styles);

const toNumber = (pos) => {
  if (pos === undefined || pos === null) return Infinity;
  const n = Number(String(pos).trim());
  return Number.isFinite(n) ? n : Infinity;
};

const sortItemsRecursively = (items) => {
  if (!Array.isArray(items)) return items;
  return [...items]
    .map((item) => ({ ...item, items: sortItemsRecursively(item.items) }))
    .sort((a, b) => toNumber(a.position) - toNumber(b.position));
};

const sortProduct = (product) => {
  if (!product) return product;
  return { ...product, items: sortItemsRecursively(product.items) };
};

const checkExtrasInvalid = (localProduct) => {
  if (!localProduct?.items?.length) return false;
  return localProduct.items.some((q) => {
    if (q.min === 0) return false;
    if (q.min > 0 && q.items.length === 0) return true;
    if (q.items?.length === 0) return false;
    const selected = q.items.filter((o) => o.selected) || [];
    if (selected[0]?.quantity >= q.min) return false;
    const total = selected.reduce((acc, o) => acc + o.quantity, 0);
    if (total < q.min || total > q.max) return true;
    return !q.items.find((o) => o.selected);
  });
};

const uncheckZeroQuantity = (obj) => {
  if (obj?.hasOwnProperty("selected") && obj?.hasOwnProperty("quantity")) {
    if (obj.quantity === 0) obj.selected = false;
  }
  for (const key in obj) {
    if (typeof obj[key] === "object") uncheckZeroQuantity(obj[key]);
  }
};

const Service = () => {
  const [cartItemIndex, setCartItemIndex] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [categories, setCategories] = useState([]);
  const [openConfirmOrder, setOpenConfirmOrder] = useState(false);
  const [loadingCatalog, setLoadingCatalog] = useState(true);

  const [localProduct, setLocalProduct] = useState(null);
  const [activeStep, setActiveStep] = useState(null);
  const [valid, setValid] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showAddresses, setShowAddresses] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [openAddAddress, setOpenAddAddress] = useState(false);
  const [invalidAddresses, setInvalidAddresses] = useState([]);
  const [allowLocation, setAllowLocation] = useState(false);
  const [deskTime, setDeskTime] = useState({ maxDesk: 0, minDesk: 0 });
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [unitAddress, setUnitAddress] = useState(null);
  const [order, setOrder] = useState(OrderStorage.getOrder());
  const [showAddNewCustomer, setShowAddNewCustomer] = useState(false);
  const [newCustomer, setNewCustomer] = useState(null);
  const [openAddCustomer, setOpenAddCustomer] = useState(false);
  const [searchCustomer, setSearchCustomer] = useState("");
  const [openCustomerCdp, setOpenCustomerCdp] = useState(false);
  const [customerId, setCustomerId] = useState(null);
  const [loadingCustomers, setLoadingCustomers] = useState(false);

  useEffect(() => {
    const initializeCatalog = (catalog) => {
      executeOnSchema(catalog, (item) => {
        item.visible = true;
        item.available = true;
      });

      Catalog.removeHiddensAndASized(catalog);

      catalog.items.forEach((category) => {
        if (category.mode !== "step") return;

        category.items.forEach((step) => {
          step.items.forEach((question) => {
            question.items.forEach((option) => {
              option.quantity = 0;

              option.items?.forEach((optionQuestion) => {
                optionQuestion.items?.forEach((nestedOption) => {
                  nestedOption.quantity = 0;
                });
              });
            });
          });
        });
      });

      return catalog;
    };

    const loadCatalog = async () => {
      const response = await CatalogAPI.getCatalogByService(null, "DELIVERY");

      const catalog = response.getData({
        items: [],
        type: "catalog",
        service: "DELIVERY",
      });

      const preparedCatalog = initializeCatalog(catalog);

      const filteredCatalog = Catalog.updateCatalogAvailability(
        preparedCatalog.items,
      );

      setCatalog(preparedCatalog);
      setCategories(filteredCatalog);
      setLoadingCatalog(false);
    };

    loadCatalog();
  }, []);

  const updateCatalogAvailability = useCallback(() => {
    setCategories((currentCategories) => {
      return Catalog.updateCatalogAvailability(currentCategories);
    });
  }, []);

  useEffect(() => {
    const availabityTimer = setInterval(() => {
      updateCatalogAvailability();
    }, 1000 * 10);

    return () => clearInterval(availabityTimer);
  }, [updateCatalogAvailability]);

  const steps = useMemo(
    () => (localProduct ? buildSteps(localProduct) : []),
    [localProduct],
  );

  useEffect(() => {
    if (!valid) return;
  }, [valid]);

  useEffect(() => {
    const run = async () => {
      if (!localProduct) {
        setValid(false);
        return;
      }
      const schema = object().shape({ quantity: number().required() });
      setValid(
        (await schema.isValid(localProduct)) &&
          !checkExtrasInvalid(localProduct),
      );
    };
    run();
  }, [localProduct]);

  useEffect(() => {
    if (
      activeStep &&
      steps.length > 0 &&
      !steps.find((s) => s.id === activeStep)
    ) {
      setActiveStep(steps[steps.length - 1]?.id ?? null);
    }
  }, [steps, activeStep]);

  const isCurrentStepValid = useMemo(() => {
    if (!activeStep || !localProduct) return false;
    if (activeStep === "sizes") return true;
    if (activeStep === "extras") return true;
    const question = findQuestion(localProduct.items, activeStep);
    if (!question || question.min === 0) return true;
    const selected = (question.items || []).reduce(
      (t, o) => (o.selected ? t + (o.quantity || 1) : t),
      0,
    );
    return selected >= question.min;
  }, [activeStep, localProduct]);

  const isLastStep = useMemo(() => {
    if (!steps.length || !activeStep) return false;
    return steps[steps.length - 1]?.id === activeStep;
  }, [steps, activeStep]);

  const onSelectProduct = (product, category) => {
    for (const item of categories) {
      if (item.id === product.parent) {
        product.extra_ingredients = [...item.extra_ingredients];
        break;
      }
    }

    const prepared = prepareProduct(
      sortProduct(JSON.parse(JSON.stringify(product))),
    );
    const initial = buildSteps(prepared);
    setLocalProduct(prepared);
    setActiveStep(initial[0]?.id ?? null);
    setSelectedCategory(category);
    setCartItemIndex(null);
  };

  const onBackToProducts = useCallback(() => {
    setLocalProduct(null);
    setActiveStep(null);
    setValid(false);
  }, []);

  const onBackToCategories = useCallback(() => {
    setLocalProduct(null);
    setActiveStep(null);
    setValid(false);
    setSelectedCategory(null);
  }, []);

  const onProductChange = useCallback((data) => {
    setLocalProduct((prev) => ({ ...prev, ...data }));
  }, []);

  const goToNextStep = useCallback(() => {
    const idx = steps.findIndex((s) => s.id === activeStep);
    if (idx < steps.length - 1) setActiveStep(steps[idx + 1].id);
  }, [steps, activeStep]);

  const goToPrevStep = useCallback(() => {
    const idx = steps.findIndex((s) => s.id === activeStep);
    if (idx > 0) setActiveStep(steps[idx - 1].id);
    else onBackToProducts();
  }, [steps, activeStep, onBackToProducts]);

  const save = useCallback(() => {
    if (!localProduct) return;
    const product = { ...localProduct };
    uncheckZeroQuantity(product);
    if (cartItemIndex !== null)
      OrderStorage.updateCartItem(cartItemIndex, product);
    else OrderStorage.addItemToCart(product);

    onBackToCategories();
  }, [localProduct, cartItemIndex, onBackToCategories]);

  const stepRefs = useRef({});

  const renderBreadcrumb = () => (
    <div className={styles.breadcrumb}>
      {selectedCategory?.name && (
        <>
          <span
            className={styles.breadcrumbStepName}
            onClick={onBackToCategories}
          >
            {selectedCategory.name}
          </span>
          <span className={styles.breadcrumbSeparator}>/</span>
        </>
      )}
      {selectedCategory && (
        <span
          className={
            localProduct
              ? styles.breadcrumbStepName
              : styles.breadcrumbLabelProduct
          }
          onClick={localProduct ? onBackToProducts : undefined}
        >
          {localProduct ? `Produto: ${localProduct.name}` : "Produto"}
        </span>
      )}
      {steps.map((step) => (
        <span key={step.id} className={styles.breadcrumbStep}>
          <span className={styles.breadcrumbSeparator}>/</span>
          <span
            ref={(el) => (stepRefs.current[step.id] = el)}
            className={css({
              breadcrumbStepName: true,
              breadcrumbActive: activeStep === step.id,
            })}
            onClick={() => setActiveStep(step.id)}
          >
            {step.name}
          </span>
        </span>
      ))}
    </div>
  );

  useEffect(() => {
    const el = stepRefs.current[activeStep];
    if (!el) return;

    el.scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "nearest",
    });
  }, [activeStep]);

  const renderFooter = () => (
    <div className={css({ footer: true })}>
      {renderBreadcrumb()}

      <div className={styles.footerActions}>
        <Button
          design="three"
          onClick={localProduct ? goToPrevStep : onBackToCategories}
          className="gap-10"
        >
          <ArrowLeftIcon />
          Voltar
        </Button>

        <div className={styles.footerRight}>
          <If cond={selectedCategory && !!localProduct}>
            <div className={styles.quantityContainer}>
              <span
                className={css({
                  minus: true,
                  disabled: localProduct?.quantity <= 1,
                })}
                onClick={() =>
                  localProduct?.quantity > 1 &&
                  onProductChange({ quantity: localProduct.quantity - 1 })
                }
              >
                <i className="fa fa-minus" />
              </span>
              <p className={styles.qty}>{localProduct?.quantity ?? 1}</p>
              <span
                className={css({ plus: true })}
                onClick={() =>
                  onProductChange({
                    quantity: (localProduct?.quantity ?? 1) + 1,
                  })
                }
              >
                <i className="fa fa-plus" />
              </span>
            </div>
          </If>

          <Button design="primary-2" onClick={save} disabled={!valid}>
            <div className="d-flex align-center justify-between gap-15">
              <i className="fal fa-shopping-cart" style={{ fontSize: 12 }} />
              <span>Adicionar ao carrinho</span>
              <span>
                {Currency.formatCurrency(
                  CatalogHelpers.calculateSelectedItemPrice(localProduct),
                )}
              </span>
            </div>
          </Button>

          {!isLastStep && (
            <Button
              design="primary"
              disabled={!isCurrentStepValid}
              onClick={goToNextStep}
            >
              Avançar
            </Button>
          )}
        </div>
      </div>
    </div>
  );

  const getCustomers = async (value) => {
    const response = await CustomerAPI.getCustomer(value);
    const data = response.getData([]);

    if (Phone.isValid(value) && (!data || data?.items?.length === 0)) {
      setCustomers([]);
      setShowAddNewCustomer(true);
      setNewCustomer({
        phone: value,
      });
      return;
    } else {
      setShowAddNewCustomer(false);
      setNewCustomer(null);
    }

    setCustomers(data?.items ?? []);
    setLoadingCustomers(false);
  };

  const debouncedGetCustomer = useCallback(
    debounce((value) => {
      if (!value) {
        setCustomers([]);
        setShowAddNewCustomer(false);
        setNewCustomer(null);
        setSelectedCustomer(null);
        setLoadingCustomers(false);
        return;
      }
      getCustomers(value);
    }, 1000),
    [],
  );

  const handleSearchCustomer = (value) => {
    setLoadingCustomers(true);
    setCustomers([]);

    debouncedGetCustomer(value);
  };

  useEffect(() => {
    return () => debouncedGetCustomer.cancel();
  }, [debouncedGetCustomer]);

  const handleSelectCustomer = async (customer) => {
    try {
      if (!customer?.id) {
        setSelectedCustomer(null);
        return;
      }

      const postCustomerResponse = await CustomerAPI.postCustomerById(
        customer?.id,
      );
      const postCustomerData = postCustomerResponse.getData([]);

      const customerId = postCustomerData?.id;

      if (!customerId) {
        setSelectedCustomer(null);
        return alert({ text: "Cliente não encontrado" });
      }

      const customerResponse = await CustomerAPI.getCustomerById(customerId);
      const customerData = customerResponse.getData(null);

      setSelectedCustomer(customerData);
      setSearchCustomer("");
      OrderStorage.setCustomer(customerData);
      setCustomers([]);
    } catch (error) {
      console.error("Erro ao selecionar cliente:", error);
      setSelectedCustomer(null);
    }
  };

  useEffect(() => {
    const orderCustomer = OrderStorage.getCustomer();
    if (orderCustomer) setSelectedCustomer(orderCustomer);
  }, []);

  useEffect(() => {
    if (!selectedCustomer) return;

    let isMounted = true;

    const delivery = OrderStorage.getDelivery();

    const customerAddresses = selectedCustomer?.extras?.addresses ?? [];

    const fetchAddressData = async () => {
      const results = await Promise.all(
        customerAddresses.map(async (address) => {
          const [nearbyUnit] =
            (await BucketsAPI.getNearbyUnits(address.geolocation)) ?? [];

          const isValid = nearbyUnit?.unit?.near;

          return {
            addressId: address.id,
            isValid,
            ...(isValid && {
              nearbyUnit: {
                ...nearbyUnit,
                id: address.id,
              },
            }),
          };
        }),
      );

      if (!isMounted) return;

      const validAddresses = results.filter((item) => item.isValid);
      const invalidAddresses = results.filter((item) => !item.isValid);

      if (!delivery?.address) {
        if (validAddresses.length > 0) {
          setSelectedAddress(validAddresses[0].nearbyUnit?.delivery?.address);
          OrderStorage.setDelivery({
            ...validAddresses[0].nearbyUnit?.delivery,
          });
        } else if (allowLocation) {
          setSelectedAddress(null);
          const delivery = {
            address: unitAddress,
            deliveryStrip: {
              price: 0,
            },
            type: DeliveryType.GET_ON_PLACE,
            total: 0,
          };
          OrderStorage.setDelivery(delivery);
        } else {
          setSelectedAddress(null);
          OrderStorage.setDelivery(null);
        }
      }

      setAddresses(validAddresses.map((item) => item.nearbyUnit));
      setInvalidAddresses(invalidAddresses.map((item) => item.addressId));
    };

    const fetchAllowLocation = async () => {
      const environment = await EnvironmentStorage.getEnvironment();

      EnvironmentsAPI.getConfigurationsDeliveryTime(environment.id).then(
        (response) => {
          const deliveryTimeData = response.getData({});
          const allowLocation = deliveryTimeData?.allowLocation;

          setAllowLocation(allowLocation);
          setDeskTime({
            maxDesk: Number(deliveryTimeData?.maxDesk),
            minDesk: Number(deliveryTimeData?.minDesk),
          });
        },
      );
    };

    fetchAddressData();
    fetchAllowLocation();

    return () => {
      isMounted = false;
    };
  }, [selectedCustomer?.name, allowLocation]);

  const refAddresses = useRef(null);

  useOutsideClick(refAddresses, () => {
    setShowAddresses(false);
  });

  useEffect(() => {
    const fetchUnitAddress = async () => {
      const environment = await EnvironmentStorage.getEnvironment();

      EnvironmentsAPI.getConfigurationsAddress(environment.id).then(
        (response) => {
          const address = response.getData({});
          if (address) setUnitAddress(address);
        },
      );
    };

    fetchUnitAddress();
  }, []);

  useEffect(() => {
    const handleOrderUpdated = () => {
      setOrder(OrderStorage.getOrder());

      const delivery = OrderStorage.getDelivery();

      if (
        delivery?.type === DeliveryType.DELIVER_ON_ADDRESS &&
        delivery?.address
      )
        setSelectedAddress(delivery?.address);

      // if (!delivery?.address) {
      //   setSelectedAddress(null);
      //   const delivery = {
      //     address: unitAddress,
      //     deliveryStrip: {
      //       price: 0,
      //     },
      //     type: DeliveryType.GET_ON_PLACE,
      //     total: 0,
      //   };
      //   OrderStorage.setDelivery(delivery);
      // }

      // const payers = OrderStorage.getPaymentPayers();
      // if (payers?.length > 0) setSelectedCustomer(payers[0].user);
    };

    handleOrderUpdated();

    window.addEventListener("orderUpdated", handleOrderUpdated);

    return () => {
      window.removeEventListener("orderUpdated", handleOrderUpdated);
    };
  }, [unitAddress]);

  const isCustomerBirthday = useMemo(() => {
    if (!selectedCustomer) return false;

    const birthday = selectedCustomer.birthdate;
    if (!birthday) return false;

    const today = dayjs().format("DD/MM");
    const birthdayDate = dayjs(birthday).format("DD/MM");

    return today === birthdayDate;
  }, [selectedCustomer]);

  const totalItems = useMemo(() => {
    return order?.cart?.catalog?.items?.reduce(
      (acc, item) => acc + item.quantity,
      0,
    );
  }, [order]);

  return (
    <>
      <Title title="Atendimento">
        <If cond={selectedCustomer || totalItems > 0}>
          <Button
            design="two"
            onClick={() => {
              OrderStorage.initOrder();
              setSelectedCustomer(null);
              setSelectedAddress(null);
              setOrder({});
            }}
          >
            Recomeçar
          </Button>
        </If>
      </Title>

      <main>
        <div className="w-100 d-flex flex-column" id="catalog">
          <div
            className={styles.customerDetails}
            onClick={(e) => {
              if (!selectedCustomer) return;

              e.stopPropagation();
              setOpenCustomerCdp(true);
              setShowAddresses(false);
            }}
          >
            <div className="d-flex align-center gap-15 w-100">
              <If cond={!selectedCustomer}>
                <svg
                  width="43"
                  height="43"
                  viewBox="0 0 43 43"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M0 21.5C0 9.62588 9.62588 0 21.5 0C33.3741 0 43 9.62588 43 21.5C43 33.3741 33.3741 43 21.5 43C9.62588 43 0 33.3741 0 21.5Z"
                    fill="#6161FF"
                    fillOpacity="0.05"
                  />
                  <path
                    d="M23.7757 18.4013C23.7757 18.0961 23.7169 17.7939 23.6024 17.5119C23.488 17.23 23.3204 16.9738 23.109 16.758C22.8976 16.5422 22.6466 16.371 22.3704 16.2543C22.0942 16.1375 21.7982 16.0774 21.4993 16.0774C21.2003 16.0774 20.9043 16.1375 20.6281 16.2543C20.3519 16.371 20.1009 16.5422 19.8896 16.758C19.6782 16.9738 19.5105 17.23 19.3961 17.5119C19.2817 17.7939 19.2228 18.0961 19.2228 18.4013C19.2228 18.7064 19.2817 19.0086 19.3961 19.2906C19.5105 19.5725 19.6782 19.8287 19.8896 20.0445C20.1009 20.2603 20.3519 20.4315 20.6281 20.5483C20.9043 20.6651 21.2003 20.7252 21.4993 20.7252C21.7982 20.7252 22.0942 20.6651 22.3704 20.5483C22.6466 20.4315 22.8976 20.2603 23.109 20.0445C23.3204 19.8287 23.488 19.5725 23.6024 19.2906C23.7169 19.0086 23.7757 18.7064 23.7757 18.4013ZM18.464 18.4013C18.464 17.5795 18.7838 16.7914 19.353 16.2103C19.9222 15.6292 20.6943 15.3027 21.4993 15.3027C22.3043 15.3027 23.0763 15.6292 23.6455 16.2103C24.2148 16.7914 24.5346 17.5795 24.5346 18.4013C24.5346 19.223 24.2148 20.0112 23.6455 20.5923C23.0763 21.1733 22.3043 21.4998 21.4993 21.4998C20.6943 21.4998 19.9222 21.1733 19.353 20.5923C18.7838 20.0112 18.464 19.223 18.464 18.4013ZM16.9463 26.9222H26.0522C26.0238 24.9929 24.4824 23.4364 22.583 23.4364H20.4156C18.5185 23.4364 16.9772 24.9929 16.9463 26.9222ZM16.1875 26.9779C16.1875 24.5935 18.0798 22.6617 20.4156 22.6617H22.583C24.9187 22.6617 26.811 24.5935 26.811 26.9779C26.811 27.3749 26.4956 27.6969 26.1067 27.6969H16.8918C16.5029 27.6969 16.1875 27.3749 16.1875 26.9779Z"
                    fill="#6D6DF2"
                  />
                </svg>

                <InputSearch
                  placeholder="Buscar ou adicionar cliente"
                  design="big"
                  className="w-100"
                  value={searchCustomer}
                  onChange={(e) => {
                    setSearchCustomer(e.target.value);
                    handleSearchCustomer(e.target.value);
                  }}
                  onClear={() => {
                    setSearchCustomer("");
                    handleSearchCustomer("");
                  }}
                />

                <If cond={searchCustomer.length > 0}>
                  <If cond={!loadingCustomers}>
                    <If cond={customers.length > 0}>
                      <div className={styles.results}>
                        <div className={styles.title}>
                          Resultados encontrados ({customers.length})
                        </div>
                        {customers.map((customer) => (
                          <div
                            className={styles.customer}
                            key={customer.id}
                            onClick={() => handleSelectCustomer(customer)}
                          >
                            <p className={styles.phone}>
                              {Phone.format(customer.phone || "")}
                            </p>
                            <p className={styles.name}>{customer.name}</p>
                          </div>
                        ))}
                      </div>
                    </If>

                    <If cond={customers.length === 0}>
                      <div className={styles.results}>
                        <div className={styles.title}>
                          Nenhum resultado encontrado
                        </div>
                      </div>
                    </If>
                  </If>

                  <If cond={loadingCustomers}>
                    <div className={styles.customersShimmer}>
                      <ul>
                        {[1, 2, 3].map((item) => (
                          <li key={item}>
                            <div className={styles.shimmerLineSmall}></div>
                            <div className={styles.shimmerLine}></div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </If>
                </If>
              </If>

              <If cond={!!selectedCustomer}>
                <svg
                  width="60"
                  height="60"
                  viewBox="0 0 60 60"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M1 30C1 13.9837 13.9837 1 30 1C46.0163 1 59 13.9837 59 30C59 46.0163 46.0163 59 30 59C13.9837 59 1 46.0163 1 30Z"
                    stroke="#333333"
                    strokeWidth="2"
                  />
                  <path
                    d="M32.4688 26.64C32.4688 26.3091 32.405 25.9814 32.2809 25.6757C32.1568 25.3699 31.975 25.0921 31.7458 24.8581C31.5166 24.6241 31.2444 24.4385 30.9449 24.3119C30.6454 24.1852 30.3244 24.12 30.0002 24.12C29.6761 24.12 29.3551 24.1852 29.0556 24.3119C28.7561 24.4385 28.4839 24.6241 28.2547 24.8581C28.0255 25.0921 27.8436 25.3699 27.7196 25.6757C27.5955 25.9814 27.5317 26.3091 27.5317 26.64C27.5317 26.971 27.5955 27.2987 27.7196 27.6044C27.8436 27.9101 28.0255 28.1879 28.2547 28.4219C28.4839 28.6559 28.7561 28.8416 29.0556 28.9682C29.3551 29.0948 29.6761 29.16 30.0002 29.16C30.3244 29.16 30.6454 29.0948 30.9449 28.9682C31.2444 28.8416 31.5166 28.6559 31.7458 28.4219C31.975 28.1879 32.1568 27.9101 32.2809 27.6044C32.405 27.2987 32.4688 26.971 32.4688 26.64ZM26.7088 26.64C26.7088 25.7489 27.0556 24.8943 27.6728 24.2642C28.2901 23.634 29.1273 23.28 30.0002 23.28C30.8732 23.28 31.7104 23.634 32.3276 24.2642C32.9449 24.8943 33.2917 25.7489 33.2917 26.64C33.2917 27.5312 32.9449 28.3858 32.3276 29.0159C31.7104 29.646 30.8732 30 30.0002 30C29.1273 30 28.2901 29.646 27.6728 29.0159C27.0556 28.3858 26.7088 27.5312 26.7088 26.64ZM25.0631 35.88H34.9374C34.9065 33.7879 33.2351 32.1 31.1754 32.1H28.8251C26.7679 32.1 25.0965 33.7879 25.0631 35.88ZM24.2402 35.9404C24.2402 33.3548 26.2922 31.26 28.8251 31.26H31.1754C33.7082 31.26 35.7602 33.3548 35.7602 35.9404C35.7602 36.3709 35.4182 36.72 34.9965 36.72H25.0039C24.5822 36.72 24.2402 36.3709 24.2402 35.9404Z"
                    fill="#6F767E"
                  />
                </svg>

                <div className="d-flex align-center w-100 justify-between">
                  <div className={styles.customerInfos}>
                    <p className={styles.name}>{selectedCustomer?.name}</p>
                    <div className="d-flex align-center gap-10">
                      <p className={styles.phone}>
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 12 12"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M10.5066 7.00675L8.35851 6.0858C7.89016 5.88278 7.33825 6.01779 7.01975 6.41284L6.41949 7.14565C5.43992 6.58237 4.61938 5.76205 4.05697 4.80282L4.79088 4.20169C5.18267 3.88188 5.31786 3.33325 5.11945 2.86621L4.19347 0.689851C3.97122 0.182346 3.4204 -0.0945934 2.88381 0.0294388L0.887695 0.49013C0.365316 0.609569 0 1.06829 0 1.60555C0 6.89715 4.30285 11.2 9.59446 11.2C10.1317 11.2 10.5904 10.8349 10.6904 10.3134L11.1511 8.31532C11.2963 7.77675 11.0163 7.2255 10.5066 7.00675ZM10.1479 8.07863L9.68655 10.0782C9.6763 10.1023 9.64041 10.15 9.59426 10.15C4.88234 10.15 1.04981 6.31748 1.04981 1.60557C1.04981 1.55976 1.07818 1.52389 1.12296 1.51363L3.12104 1.0525C3.12788 1.05079 3.13505 1.0501 3.14189 1.0501C3.17982 1.0501 3.21537 1.07301 3.23042 1.10718L4.15224 3.25816C4.16865 3.29679 4.15771 3.3419 4.1249 3.3689L3.05815 4.22422C2.86948 4.37871 2.81205 4.64291 2.91937 4.86167C3.64147 6.33233 4.85008 7.54138 6.32096 8.26326C6.52003 8.37058 6.80419 8.31315 6.95885 8.12448L7.83407 7.05544C7.85833 7.02468 7.90379 7.01305 7.94174 7.03083L10.0912 7.95156C10.1326 7.98894 10.1566 8.03488 10.1479 8.07863Z"
                            fill="#6F767E"
                          />
                        </svg>
                        <span>{Phone.mask(selectedCustomer?.phone || "")}</span>
                      </p>
                      <If cond={!!isCustomerBirthday}>
                        <div className={styles.birthday}>
                          <i className="fal fa-birthday-cake" />
                        </div>
                      </If>
                    </div>
                  </div>

                  <div className={styles.addressContainer}>
                    <p className={styles.label}>ENDEREÇO DE ENTREGA</p>
                    <div
                      className={css(styles.selectedAddress, {
                        [styles.open]: showAddresses,
                      })}
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowAddresses(!showAddresses);
                      }}
                      ref={refAddresses}
                    >
                      <div className={styles.address}>
                        <span>
                          {selectedAddress
                            ? formatAddress(selectedAddress)
                            : allowLocation
                              ? "Retirar na loja física"
                              : "Selecione um endereço"}
                        </span>
                        <i className="fas fa-chevron-down" />
                      </div>
                      <If cond={!!showAddresses}>
                        <div className={styles.addresses}>
                          <If cond={allowLocation}>
                            <div
                              className={styles.address}
                              onClick={(e) => {
                                e.stopPropagation();
                                const delivery = {
                                  address: unitAddress,
                                  deliveryStrip: {
                                    price: 0,
                                  },
                                  type: DeliveryType.GET_ON_PLACE,
                                  total: 0,
                                };
                                OrderStorage.setDelivery(delivery);
                                setSelectedAddress(null);
                                setSelectedUnit(null);
                                setShowAddresses(false);
                              }}
                            >
                              Retirar na loja física
                            </div>
                          </If>
                          {(selectedCustomer?.extras?.addresses || [])?.map(
                            (address) => {
                              const isInvalid = invalidAddresses.includes(
                                address.id,
                              );

                              const sameUnit = addresses.find(
                                (a) => a.id === address.id,
                              );

                              return (
                                <div
                                  className={css(styles.address, {
                                    [styles.invalid]: isInvalid,
                                  })}
                                  key={address.id}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (isInvalid) return;

                                    const delivery = sameUnit?.delivery;
                                    OrderStorage.setDelivery({
                                      ...delivery,
                                      address,
                                    });

                                    setSelectedAddress(address);
                                    setSelectedUnit(sameUnit);
                                    setShowAddresses(false);
                                  }}
                                  title={formatAddress(address)}
                                >
                                  {formatAddress(address)}
                                </div>
                              );
                            },
                          )}
                          <If
                            cond={
                              (selectedCustomer?.extras?.addresses?.length ||
                                0) < 10
                            }
                          >
                            <div
                              className={styles.addAddress}
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowAddresses(false);
                                setOpenAddAddress(true);
                              }}
                            >
                              <span>Adicionar endereço</span>
                            </div>
                          </If>
                        </div>
                      </If>
                    </div>
                  </div>
                </div>
              </If>
            </div>
          </div>

          <If cond={!showAddNewCustomer}>
            <div className={styles.catalogContainer}>
              <If cond={loadingCatalog}>
                <div className={styles.loading}>
                  <Loading />
                </div>
              </If>
              <If cond={!localProduct}>
                <Categories
                  key={selectedCategory?.name ?? "none"}
                  categories={categories}
                  onSelectCategory={setSelectedCategory}
                  onSelectProduct={onSelectProduct}
                  initialCategory={selectedCategory}
                />
              </If>

              <If cond={!!localProduct}>
                <ProductDetails
                  localProduct={localProduct}
                  activeStep={activeStep}
                  onProductChange={onProductChange}
                />
              </If>

              <If cond={!!selectedCategory}>{renderFooter()}</If>
            </div>
          </If>

          <If cond={showAddNewCustomer}>
            <div className={styles.addCustomerContainer}>
              <div className={styles.addCustomer}>
                <svg
                  width="63"
                  height="63"
                  viewBox="0 0 63 63"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M1.5 31.5C1.5 14.9315 14.9315 1.5 31.5 1.5C48.0685 1.5 61.5 14.9315 61.5 31.5C61.5 48.0685 48.0685 61.5 31.5 61.5C14.9315 61.5 1.5 48.0685 1.5 31.5Z"
                    fill="#6161FF"
                    fillOpacity="0.1"
                  />
                  <path
                    d="M31.5 0.75C48.4828 0.75 62.25 14.5172 62.25 31.5C62.25 48.4828 48.4828 62.25 31.5 62.25C14.5172 62.25 0.75 48.4828 0.75 31.5C0.75 14.5172 14.5172 0.75 31.5 0.75Z"
                    stroke="#6161FF"
                    strokeOpacity="0.3"
                    strokeWidth="1.5"
                  />
                  <path
                    d="M28.8 25.2C29.1546 25.2 29.5057 25.2698 29.8332 25.4055C30.1608 25.5412 30.4585 25.7401 30.7092 25.9908C30.9599 26.2415 31.1588 26.5392 31.2945 26.8667C31.4302 27.1943 31.5 27.5454 31.5 27.9C31.5 28.2546 31.4302 28.6057 31.2945 28.9332C31.1588 29.2608 30.9599 29.5585 30.7092 29.8092C30.4585 30.0599 30.1608 30.2588 29.8332 30.3945C29.5057 30.5301 29.1546 30.6 28.8 30.6C28.4454 30.6 28.0943 30.5301 27.7668 30.3945C27.4392 30.2588 27.1415 30.0599 26.8908 29.8092C26.6401 29.5585 26.4412 29.2608 26.3055 28.9332C26.1698 28.6057 26.1 28.2546 26.1 27.9C26.1 27.5454 26.1698 27.1943 26.3055 26.8667C26.4412 26.5392 26.6401 26.2415 26.8908 25.9908C27.1415 25.7401 27.4392 25.5412 27.7668 25.4055C28.0943 25.2698 28.4454 25.2 28.8 25.2ZM28.8 31.5C29.7548 31.5 30.6705 31.1207 31.3456 30.4456C32.0207 29.7704 32.4 28.8548 32.4 27.9C32.4 26.9452 32.0207 26.0295 31.3456 25.3544C30.6705 24.6793 29.7548 24.3 28.8 24.3C27.8452 24.3 26.9295 24.6793 26.2544 25.3544C25.5793 26.0295 25.2 26.9452 25.2 27.9C25.2 28.8548 25.5793 29.7704 26.2544 30.4456C26.9295 31.1207 27.8452 31.5 28.8 31.5ZM27.5147 33.75H30.0853C32.3353 33.75 34.1634 35.5584 34.2 37.8H23.4C23.4337 35.5584 25.2619 33.75 27.5147 33.75ZM27.5147 32.85C24.7444 32.85 22.5 35.0944 22.5 37.8647C22.5 38.3259 22.8741 38.7 23.3353 38.7H34.2647C34.7259 38.7 35.1 38.3259 35.1 37.8647C35.1 35.0944 32.8556 32.85 30.0853 32.85H27.5147ZM36.9 32.85C36.9 33.0975 37.1025 33.3 37.35 33.3C37.5975 33.3 37.8 33.0975 37.8 32.85V30.6H40.05C40.2975 30.6 40.5 30.3975 40.5 30.15C40.5 29.9025 40.2975 29.7 40.05 29.7H37.8V27.45C37.8 27.2025 37.5975 27 37.35 27C37.1025 27 36.9 27.2025 36.9 27.45V29.7H34.65C34.4025 29.7 34.2 29.9025 34.2 30.15C34.2 30.3975 34.4025 30.6 34.65 30.6H36.9V32.85Z"
                    fill="#4C4CFF"
                  />
                  <path
                    d="M28.8 25.2C29.1546 25.2 29.5057 25.2698 29.8332 25.4055C30.1608 25.5412 30.4585 25.7401 30.7092 25.9908C30.9599 26.2415 31.1588 26.5392 31.2945 26.8667C31.4302 27.1943 31.5 27.5454 31.5 27.9C31.5 28.2546 31.4302 28.6057 31.2945 28.9332C31.1588 29.2608 30.9599 29.5585 30.7092 29.8092C30.4585 30.0599 30.1608 30.2588 29.8332 30.3945C29.5057 30.5301 29.1546 30.6 28.8 30.6C28.4454 30.6 28.0943 30.5301 27.7668 30.3945C27.4392 30.2588 27.1415 30.0599 26.8908 29.8092C26.6401 29.5585 26.4412 29.2608 26.3055 28.9332C26.1698 28.6057 26.1 28.2546 26.1 27.9C26.1 27.5454 26.1698 27.1943 26.3055 26.8667C26.4412 26.5392 26.6401 26.2415 26.8908 25.9908C27.1415 25.7401 27.4392 25.5412 27.7668 25.4055C28.0943 25.2698 28.4454 25.2 28.8 25.2ZM28.8 31.5C29.7548 31.5 30.6705 31.1207 31.3456 30.4456C32.0207 29.7704 32.4 28.8548 32.4 27.9C32.4 26.9452 32.0207 26.0295 31.3456 25.3544C30.6705 24.6793 29.7548 24.3 28.8 24.3C27.8452 24.3 26.9295 24.6793 26.2544 25.3544C25.5793 26.0295 25.2 26.9452 25.2 27.9C25.2 28.8548 25.5793 29.7704 26.2544 30.4456C26.9295 31.1207 27.8452 31.5 28.8 31.5ZM27.5147 33.75H30.0853C32.3353 33.75 34.1634 35.5584 34.2 37.8H23.4C23.4337 35.5584 25.2619 33.75 27.5147 33.75ZM27.5147 32.85C24.7444 32.85 22.5 35.0944 22.5 37.8647C22.5 38.3259 22.8741 38.7 23.3353 38.7H34.2647C34.7259 38.7 35.1 38.3259 35.1 37.8647C35.1 35.0944 32.8556 32.85 30.0853 32.85H27.5147ZM36.9 32.85C36.9 33.0975 37.1025 33.3 37.35 33.3C37.5975 33.3 37.8 33.0975 37.8 32.85V30.6H40.05C40.2975 30.6 40.5 30.3975 40.5 30.15C40.5 29.9025 40.2975 29.7 40.05 29.7H37.8V27.45C37.8 27.2025 37.5975 27 37.35 27C37.1025 27 36.9 27.2025 36.9 27.45V29.7H34.65C34.4025 29.7 34.2 29.9025 34.2 30.15C34.2 30.3975 34.4025 30.6 34.65 30.6H36.9V32.85Z"
                    fill="white"
                    fillOpacity="0.2"
                  />
                </svg>

                <p className={styles.phone}>
                  {Phone.mask(newCustomer?.phone || "")}
                </p>
                <p className={styles.label}>
                  Este cliente não está cadastrado no sistema.
                </p>
                <Hairline className="mt-20 mb-20" />
                <Button
                  design="primary"
                  onClick={() => setOpenAddCustomer(true)}
                >
                  Cadastrar novo cliente
                </Button>
              </div>
            </div>
          </If>
        </div>

        <Cart
          onOpenConfirmOrder={() => setOpenConfirmOrder(true)}
          selectedCustomer={selectedCustomer}
        />

        <ConfirmOrder
          open={openConfirmOrder}
          onClose={() => setOpenConfirmOrder(false)}
          selectedCustomer={selectedCustomer}
          selectedUnit={selectedUnit}
          unitAddress={unitAddress}
          allowLocation={allowLocation}
          order={order}
          maxDesk={deskTime.maxDesk}
          minDesk={deskTime.minDesk}
          onChangeCustomerId={(id) => setCustomerId(id)}
        />

        <AddCustomer
          open={openAddCustomer}
          onClose={() => setOpenAddCustomer(false)}
          newCustomer={newCustomer}
          onCustomerCreated={(customer) => {
            handleSelectCustomer(customer);
            setOpenAddCustomer(false);
            setShowAddNewCustomer(false);
          }}
        />

        <AddAddress
          open={openAddAddress}
          onClose={() => setOpenAddAddress(false)}
          customer={selectedCustomer}
          onAddAddress={async (address) => {
            const c = selectedCustomer;
            const updatedCustomer = {
              birthdate: c.birthdate,
              name: c.name,
              phone: c.phone,
              email: c.email || null,
              extras: {
                doc: c?.extras?.doc,
                addresses: [...(c?.extras?.addresses ?? []), address],
              },
            };

            const response = await CustomerAPI.updateCustomer(
              c.id,
              updatedCustomer,
            );

            const data = response.getData({});

            if (data.success) {
              setSelectedCustomer(updatedCustomer);
              OrderStorage.setCustomer(updatedCustomer);
            }
          }}
        />
      </main>

      <CustomerDrawer
        visible={!!openCustomerCdp}
        customerId={customerId}
        brandId={null}
        unitId={null}
        onClose={() => setOpenCustomerCdp(false)}
      />
    </>
  );
};

export default Service;
